import { useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { AUDIT_ACTION_FILTER_PATTERN, formatEatDate, formatEatTime } from '@wodi/domain';
import {
  Button,
  Cell,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState, type SyntheticEvent } from 'react';

import { auditEventsPage, verifyAuditChain, type AuditFilters } from '../api/privacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { staffQuery } from '../api/sha.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { errorText } from './labels-settings-page.tsx';
import styles from './privacy.module.css';

// Settings > Audit log (docs/plans/golive-privacy-tooling.md §3): search the audit log by
// person, action, record and days, newest first. Read-only. Every search and every chain check
// is itself recorded in the audit log, by the server.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ChainCheck() {
  const online = useOnline();
  const check = useMutation({ mutationFn: verifyAuditChain });
  return (
    <Section
      title="Tamper check"
      description="Each event carries a fingerprint of the one before it. Checking reads the whole log."
    >
      <div className={styles.chain}>
        <Button
          isDisabled={!online}
          isPending={check.isPending}
          pendingLabel="Checking…"
          onPress={() => {
            check.mutate();
          }}
        >
          Verify chain
        </Button>
        {check.data === undefined ? null : check.data.intact ? (
          <Tag state="done">{`Chain intact (${String(check.data.checked)} events checked)`}</Tag>
        ) : (
          <Tag state="failed">{`Chain broken at event ${String(check.data.firstBrokenSeq)}`}</Tag>
        )}
      </div>
      {check.data !== undefined && !check.data.intact ? (
        <Notice tone="error">
          {`Event ${String(check.data.firstBrokenSeq)} doesn't match the events before it, so the log was changed outside Wodi from that point. Tell the engineering lead and the data protection officer now.`}
        </Notice>
      ) : null}
      {check.error === null ? null : (
        <Notice tone="error">
          {errorText(check.error, 'The check could not run. Try again.')}
        </Notice>
      )}
    </Section>
  );
}

function Filters({
  onSearch,
  mayListPeople,
}: {
  onSearch: (filters: AuditFilters) => void;
  mayListPeople: boolean;
}) {
  const people = useQuery({ ...staffQuery, enabled: mayListPeople });
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [entityId, setEntityId] = useState('');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const actionValid = action === '' || AUDIT_ACTION_FILTER_PATTERN.test(action.trim());
  const recordValid = entityId === '' || UUID.test(entityId.trim());
  const daysValid = from === null || to === null || from <= to;
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!actionValid || !recordValid || !daysValid) return;
    onSearch({
      ...(actorId === '' ? {} : { actorId }),
      ...(action.trim() === '' ? {} : { action: action.trim() }),
      ...(entityId.trim() === '' ? {} : { entityId: entityId.trim().toLowerCase() }),
      ...(from === null ? {} : { from }),
      ...(to === null ? {} : { to }),
    });
  };
  return (
    <Section title="Search">
      <form aria-label="Search the audit log" onSubmit={submit}>
        <div className={styles.filters}>
          {mayListPeople ? (
            <Select
              label="Person"
              options={[
                { id: '', label: 'Anyone' },
                ...(people.data?.items ?? []).map((u) => ({ id: u.id, label: u.displayName })),
              ]}
              value={actorId}
              onChange={(v) => {
                setActorId(typeof v === 'string' ? v : '');
              }}
            />
          ) : null}
          <TextField
            label="Action"
            description="Like record.break_glass, or record. for all record actions"
            value={action}
            onChange={setAction}
            {...(actionValid ? {} : { errorMessage: 'Lower-case words joined by dots.' })}
          />
          <TextField
            label="Record ID"
            value={entityId}
            onChange={setEntityId}
            {...(recordValid ? {} : { errorMessage: 'Paste the whole ID.' })}
          />
          <DateField label="From" value={from} onChange={setFrom} />
          <DateField
            label="To"
            value={to}
            onChange={setTo}
            {...(daysValid ? {} : { errorMessage: 'The end is before the start.' })}
          />
        </div>
        <div className={styles.actions}>
          <Button type="submit" variant="primary">
            Search
          </Button>
        </div>
      </form>
      {people.isError ? (
        <p className={styles.meta}>
          The list of people couldn&apos;t be loaded; search by the other fields.
        </p>
      ) : null}
    </Section>
  );
}

function Results({ filters }: { filters: AuditFilters }) {
  const online = useOnline();
  const pages = useInfiniteQuery({
    queryKey: ['privacy', 'audit', filters],
    queryFn: ({ pageParam }) => auditEventsPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Every read is audited, so read only when asked.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  if (pages.isPending) return <LoadingBlock label="Searching the audit log" rows={5} />;
  if (pages.isError)
    return (
      <Notice tone="error" action={<Button onPress={() => void pages.refetch()}>Try again</Button>}>
        {errorText(pages.error, 'The audit log could not be searched. Try again.')}
      </Notice>
    );
  const items = pages.data.pages.flatMap((p) => p.items);
  return (
    <Section
      title="Events"
      description="Newest first. Opening this list is recorded in the audit log."
    >
      {items.length === 0 ? (
        <p className={styles.meta}>No events match. Widen the search.</p>
      ) : (
        <TableScroll>
          <Table aria-label="Audit events" flat>
            <TableHeader>
              <Column isRowHeader>When</Column>
              <Column>Event</Column>
              <Column>Who</Column>
              <Column>Action</Column>
              <Column>Record</Column>
              <Column>Reason</Column>
            </TableHeader>
            <TableBody items={items}>
              {(e) => {
                const at = new Date(e.occurredAt);
                return (
                  <Row id={e.id}>
                    <Cell>
                      <span className={styles.numbers}>
                        {`${formatEatDate(at)} ${formatEatTime(at)}`}
                      </span>
                    </Cell>
                    <Cell>
                      <span className={styles.numbers}>{String(e.seq)}</span>
                    </Cell>
                    <Cell>{e.actorName}</Cell>
                    <Cell>
                      <span className={styles.mono}>{e.action}</span>
                    </Cell>
                    <Cell>
                      {e.entityId === null ? (
                        '—'
                      ) : (
                        <span
                          className={styles.mono}
                        >{`${e.entityType ?? 'record'} ${e.entityId}`}</span>
                      )}
                    </Cell>
                    <Cell>{e.reason ?? '—'}</Cell>
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </TableScroll>
      )}
      {pages.hasNextPage ? (
        <div className={styles.actions}>
          <Button
            isDisabled={!online}
            isPending={pages.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void pages.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

export function AuditLogPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [filters, setFilters] = useState<AuditFilters | null>(null);
  if (session === null || !can(session, 'audit.read'))
    return (
      <SettingsPage title="Audit log">
        <Notice tone="info">You don&apos;t have permission to read the audit log.</Notice>
      </SettingsPage>
    );
  return (
    <SettingsPage title="Audit log">
      {!online ? (
        <Notice tone="info">No connection to Wodi. Searching and checking wait for it.</Notice>
      ) : null}
      <Filters onSearch={setFilters} mayListPeople={can(session, 'admin.users.read')} />
      {filters === null ? (
        <p className={styles.meta}>Search to see events. Leave every field empty for the latest.</p>
      ) : (
        <Results filters={filters} />
      )}
      <ChainCheck />
    </SettingsPage>
  );
}
